/**
 * /productos/nuevo  → modo create.
 * /productos/:id    → modo edit (pre-fill via GET).
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { productApi, type ProductInput } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

const EMPTY: ProductInput = {
  sku: '',
  name: '',
  description: '',
  unit_price: '',
  currency: 'COP',
  unit_code: 'NIU',
  tax_iva_percent: '19.00',
  tax_iva_excluded: false,
  tax_inc_percent: '0.00',
  standard_code: '',
  standard_code_scheme: '',
}

const IVA_OPTIONS = [
  { value: '0.00', label: '0% (exento o ventas en habilitación)' },
  { value: '5.00', label: '5%' },
  { value: '19.00', label: '19% (general)' },
] as const

const INC_OPTIONS = [
  { value: '0.00', label: 'Sin INC' },
  { value: '4.00', label: '4% (telefonía)' },
  { value: '8.00', label: '8% (cervezas, boletería)' },
  { value: '16.00', label: '16% (vehículos lujo, embarcaciones)' },
] as const

export function ProductEditPage() {
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id && id !== 'nuevo'
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const [s, setS] = useState<ProductInput>(EMPTY)

  const get = useQuery({
    queryKey: ['product', activeOrgId, id],
    queryFn: () => productApi.get(activeOrgId!, id!),
    enabled: !!activeOrgId && isEdit,
  })

  useEffect(() => {
    if (get.data) {
      setS({
        sku: get.data.sku ?? '',
        name: get.data.name,
        description: get.data.description ?? '',
        unit_price: get.data.unit_price,
        currency: get.data.currency,
        unit_code: get.data.unit_code,
        tax_iva_percent: get.data.tax_iva_percent,
        tax_iva_excluded: get.data.tax_iva_excluded,
        tax_inc_percent: get.data.tax_inc_percent,
        standard_code: get.data.standard_code ?? '',
        standard_code_scheme: get.data.standard_code_scheme ?? '',
      })
    }
  }, [get.data])

  const save = useMutation({
    mutationFn: () =>
      isEdit ? productApi.update(activeOrgId!, id!, s) : productApi.create(activeOrgId!, s),
    onSuccess: () => {
      toast.success(isEdit ? 'Producto actualizado' : 'Producto creado')
      nav(ROUTES.PRODUCTS)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(`Error: ${e?.response?.data?.message ?? e?.message ?? 'unknown'}`)
    },
  })

  const archive = useMutation({
    mutationFn: () => productApi.archive(activeOrgId!, id!),
    onSuccess: () => {
      toast.success('Producto archivado')
      nav(ROUTES.PRODUCTS)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo archivar: ${e?.response?.data?.message ?? 'error'}`)
    },
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  const valid = s.name.trim() !== '' && s.unit_price.trim() !== '' && parseFloat(s.unit_price) > 0

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <Link
          to={ROUTES.PRODUCTS}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>
        <h1 className="mt-2 text-3xl font-bold">
          {isEdit ? 'Editar producto' : 'Nuevo producto'}
        </h1>

        {get.isLoading && (
          <div className="mt-6 flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) save.mutate()
          }}
          className="mt-6 space-y-4 rounded-xl border border-border bg-bg-surface p-6"
        >
          <Field label="Nombre *" htmlFor="name">
            <input
              id="name"
              type="text"
              value={s.name}
              onChange={(e) => setS({ ...s, name: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="SKU (opcional)" htmlFor="sku">
            <input
              id="sku"
              type="text"
              value={s.sku ?? ''}
              onChange={(e) => setS({ ...s, sku: e.target.value })}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-fg-muted">Único per facturador.</p>
          </Field>

          <Field label="Descripción" htmlFor="description">
            <textarea
              id="description"
              value={s.description ?? ''}
              onChange={(e) => setS({ ...s, description: e.target.value })}
              className={inputCls}
              rows={2}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Precio unitario (COP) *" htmlFor="unit_price">
              <input
                id="unit_price"
                type="text"
                inputMode="decimal"
                value={s.unit_price}
                onChange={(e) =>
                  setS({ ...s, unit_price: e.target.value.replace(/[^\d.]/g, '') })
                }
                className={`${inputCls} font-mono`}
                placeholder="100000.00"
              />
            </Field>
            <Field label="Unidad de medida (UBL)" htmlFor="unit_code">
              <input
                id="unit_code"
                type="text"
                value={s.unit_code ?? 'NIU'}
                onChange={(e) => setS({ ...s, unit_code: e.target.value.toUpperCase() })}
                className={`${inputCls} font-mono`}
                maxLength={4}
              />
              <p className="mt-1 text-xs text-fg-muted">
                NIU=unidad, KGM=kg, MTR=metro, HUR=hora, etc.
              </p>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="IVA por defecto" htmlFor="iva">
              <select
                id="iva"
                value={s.tax_iva_percent ?? '19.00'}
                onChange={(e) => setS({ ...s, tax_iva_percent: e.target.value })}
                className={inputCls}
                disabled={s.tax_iva_excluded}
              >
                {IVA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label className="mt-2 flex items-center gap-2 text-xs text-fg-secondary">
                <input
                  type="checkbox"
                  checked={s.tax_iva_excluded ?? false}
                  onChange={(e) => setS({ ...s, tax_iva_excluded: e.target.checked })}
                />
                Producto excluido de IVA (régimen especial)
              </label>
            </Field>

            <Field label="INC (impuesto al consumo)" htmlFor="inc">
              <select
                id="inc"
                value={s.tax_inc_percent ?? '0.00'}
                onChange={(e) => setS({ ...s, tax_inc_percent: e.target.value })}
                className={inputCls}
              >
                {INC_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <p className="text-xs text-fg-muted">
            Estos defaults se cargan al agregar el producto a una factura. Se pueden
            sobrescribir per-línea (ej: exportación a 0% sin tocar el catálogo).
          </p>

          <div className="flex items-center justify-between pt-2">
            {isEdit && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('¿Archivar este producto? Las facturas históricas lo conservan.')) {
                    archive.mutate()
                  }
                }}
                className="inline-flex items-center gap-1.5 text-xs text-error-fg hover:underline"
              >
                <Trash2 className="h-3.5 w-3.5" /> Archivar
              </button>
            )}
            <div className="ml-auto">
              <button
                type="submit"
                disabled={!valid || save.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {save.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> {isEdit ? 'Guardar cambios' : 'Crear producto'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

const inputCls =
  'block w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary placeholder-fg-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
